import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import authClient from '../auth/authClient';
import { Building2 } from 'lucide-react';

const ACCOUNT_APP_URL = import.meta.env.VITE_ACCOUNT_URL || 'https://news-assist-account.kardoe.workers.dev';

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );
}

function getOrgInitials(name: string) {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function HomePage() {
  const navigate = useNavigate();
  const { data: organizations, isPending: isLoading } = authClient.useListOrganizations();

  useEffect(() => {
    // Auto-redirect if user has exactly one org
    if (!isLoading && organizations && organizations.length === 1 && organizations[0]) {
      navigate(`/${organizations[0].slug}`, { replace: true });
    }
  }, [organizations, isLoading, navigate]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // No organizations
  if (!organizations || organizations.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-gray-400" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              No Organizations
            </h1>
            <p className="text-gray-600 mb-6">
              You're not a member of any organizations yet. Create one or ask an admin to invite you.
            </p>
            <a
              href={`${ACCOUNT_APP_URL}/organizations/new`}
              className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Organization
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Multiple organizations - show picker
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Select Organization
          </h1>
          <p className="text-gray-600 mt-2">
            Choose an organization to continue
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {organizations.map((org) => (
            <Link
              key={org.id}
              to={`/${org.slug}`}
              className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6 flex items-center gap-4"
            >
              {org.logo ? (
                <img
                  src={org.logo}
                  alt={org.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold">
                  {getOrgInitials(org.name)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h2 className="font-medium text-gray-900 truncate">
                  {org.name}
                </h2>
                <p className="text-sm text-gray-500">
                  @{org.slug}
                </p>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8 text-center">
          <a
            href={`${ACCOUNT_APP_URL}/organizations/new`}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            Create new organization
          </a>
        </div>
      </div>
    </div>
  );
}
